
import React from 'react'
import { motion } from 'motion/react'
import { useWalletBalance } from '@/hooks/useWalletBalance'
import { useSession } from '@/lib/auth-client'
import { Button } from '@/components/ui/button'
import { Icon } from '@/components/ui/icon'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { cn } from '@/lib/utils'

interface WalletBalanceProps {
  className?: string
}

export const WalletBalance: React.FC<WalletBalanceProps> = ({ className }) => {
  const session = useSession()
  const isAuthenticated = !!session.data?.user
  const { balance, isLoading, error, refetch } = useWalletBalance()

  if (!isAuthenticated) return null

  const formattedBalance = balance
    ? `$${parseFloat(balance).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`
    : null

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            'gap-1.5 font-medium text-sm tabular-nums',
            className,
          )}
        >
          <Icon name="wallet" className="h-4 w-4" />
          {isLoading ? (
            <span className="inline-block h-3 w-10 animate-pulse rounded bg-muted" />
          ) : error ? (
            <span className="text-muted-foreground">--</span>
          ) : (
            <motion.span
              key={formattedBalance}
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
            >
              {formattedBalance}
            </motion.span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-56 p-3">
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <p className="text-xs text-muted-foreground">Wallet Balance</p>
            {error ? (
              <div className="flex items-center gap-2">
                <span className="text-destructive text-sm">Failed to load</span>
                <Button variant="ghost" size="sm" onClick={refetch}>
                  Retry
                </Button>
              </div>
            ) : (
              <p className="text-xl font-semibold text-foreground">
                {isLoading ? <span className="inline-block h-5 w-16 animate-pulse rounded bg-muted" /> : formattedBalance}
              </p>
            )}
          </div>
          {!error && (
            <p className="text-xs text-muted-foreground text-center">
              Pay-as-you-go — charged per generation
            </p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
